import { Square, emptySquareData, type SquareData } from './Square';
import { Obstacle } from './Obstacle';
import { MegaPuppy, type MegaPuppySnapshot } from './MegaPuppy';
import type { PuppyId, Color } from './types';
import type { Rng } from './rng';

// Mirrors iOS Board.swift L734-745. Index = level number - 1 (1-based input).
// Beyond entry 100, levels keep the entry-100 obstacle count.
const NUM_OBSTACLES_PER_LEVEL: readonly number[] = [
  0, 1, 0, 1, 0, 0, 1, 1, 0, 1,
  1, 0, 2, 0, 0, 2, 0, 1, 0, 1,
  1, 1, 1, 1, 1, 1, 1, 1, 1, 1,
  0, 2, 2, 2, 2, 1, 1, 1, 1, 1,
  1, 2, 0, 1, 2, 0, 1, 2, 0, 1,
  1, 1, 1, 1, 1, 1, 1, 1, 1, 1,
  1, 1, 1, 1, 1, 1, 1, 1, 1, 1,
  1, 1, 1, 1, 1, 2, 2, 2, 2, 2,
  3, 1, 1, 1, 2, 1, 2, 2, 2, 2,
  2, 2, 2, 2, 2, 2, 2, 2, 3, 3,
  3,
];

// Mirrors iOS Board.swift::randomObstacleInstances — only these board cells
// are eligible obstacle locations.
const OBSTACLE_INDEX_POOL: readonly number[] = [
  8, 10, 12, 22, 26, 29, 33, 43, 45, 47,
];

// Result of canPlacePuppy. Mirrors Square.swift::canPlacePuppy's tuple:
// (canPlace, score, on, top, right, bottom, left)
// `on` is the focal square's index when the placement is rejected outright;
// `top/right/bottom/left` are the neighbor indexes that blocked the placement
// (-1 when not blocking on that side).
export interface PlacementResult {
  canPlace: boolean;
  score: number;
  on: number;
  top: number;
  right: number;
  bottom: number;
  left: number;
}

const REJECT_NO_NEIGHBORS = (idx: number): PlacementResult => ({
  canPlace: false,
  score: 0,
  on: idx,
  top: -1,
  right: -1,
  bottom: -1,
  left: -1,
});

export interface BoardSnapshot {
  numSquaresHoriz: number;
  numSquaresVert: number;
  squares: SquareData[];
  obstacles: { id: number; atIndex: number }[];
  bonusSquare: number;
  megaPuppies?: MegaPuppySnapshot[];
}

export class Board {
  readonly numSquaresHoriz: number;
  readonly numSquaresVert: number;
  readonly numSquares: number;
  readonly squares: Square[];
  obstacles: Obstacle[] = [];
  megaPuppies: MegaPuppy[] = [];
  bonusSquare = -1;

  constructor(numSquaresHoriz: number, numSquaresVert: number) {
    this.numSquaresHoriz = numSquaresHoriz;
    this.numSquaresVert = numSquaresVert;
    this.numSquares = numSquaresHoriz * numSquaresVert;
    this.squares = Array.from({ length: this.numSquares }, (_, i) =>
      new Square(emptySquareData(i), numSquaresHoriz, this.numSquares),
    );
  }

  squareAt(index: number): Square {
    const sq = this.squares[index];
    if (!sq) throw new Error(`square index out of range: ${index}`);
    return sq;
  }

  // True if any obstacle covers this index. Mirrors Board.swift::obstacleAtIndex.
  obstacleAtIndex(index: number): boolean {
    return this.obstacles.some((o) => o.indexInObstacle(index, this.numSquaresHoriz));
  }

  // Mirrors Board.swift::isClearedAndFree:
  //   true iff the square is cleared, empty (no puppy), and not under a MegaPuppy.
  isClearedAndFree(index: number): boolean {
    const sq = this.squareAt(index);
    if (!sq.data.isClear) return false;
    if (sq.data.puppy !== null || sq.data.isFireHydrant) return false;
    if (sq.data.underCrazyMega) return false;
    return true;
  }

  // Mirrors Square.swift::canPlacePuppy. The focal square is `index`; we evaluate
  // each occupied neighbor with calcPlaceScore and aggregate.
  canPlacePuppy(
    index: number,
    placePuppy: PuppyId,
    placeColor: Color,
    placeIsCrazy: boolean,
    placeIsHydrant: boolean,
  ): PlacementResult {
    const sq = this.squareAt(index);

    if (sq.data.puppy !== null || sq.data.isFireHydrant) return REJECT_NO_NEIGHBORS(index);
    if (this.obstacleAtIndex(index)) return REJECT_NO_NEIGHBORS(index);
    if (sq.data.underCrazyMega) return REJECT_NO_NEIGHBORS(index);

    let aggregateScore = 0;
    let top = -1;
    let right = -1;
    let bottom = -1;
    let left = -1;
    let cantPlay = false;

    const consider = (
      neighborIdx: number,
      assignBlock: (n: number) => void,
    ): void => {
      if (neighborIdx < 0) return;
      const r = this.calcPlaceScore(neighborIdx, placePuppy, placeColor, placeIsCrazy, placeIsHydrant);
      if (!r.canPlace) {
        assignBlock(neighborIdx);
        cantPlay = true;
      } else {
        aggregateScore += r.score;
      }
    };

    consider(sq.leftSqNdx, (n) => (left = n));
    consider(sq.topSqNdx, (n) => (top = n));
    consider(sq.rightSqNdx, (n) => (right = n));
    consider(sq.bottomSqNdx, (n) => (bottom = n));

    if (cantPlay) {
      return { canPlace: false, score: 0, on: -1, top, right, bottom, left };
    }
    if (aggregateScore === 0) {
      return REJECT_NO_NEIGHBORS(index);
    }

    return { canPlace: true, score: aggregateScore, on: -1, top, right, bottom, left };
  }

  // Mirrors Square.swift::calcPlaceScore — per-neighbor matching + scoring.
  // The math: 0 for non-matches; +5 hydrant-adjacent; +10 color match;
  // +10 puppy match; +20 if both color and puppy match.
  calcPlaceScore(
    neighborIdx: number,
    placePuppy: PuppyId,
    placeColor: Color,
    placeIsCrazy: boolean,
    placeIsHydrant: boolean,
  ): { canPlace: boolean; score: number } {
    const n = this.squareAt(neighborIdx).data;
    let score = 0;

    if (placeIsHydrant) {
      if (n.isFireHydrant || n.isCrazy || n.underCrazyMega) score += 5;
      else if (n.puppy !== null) score += 10;
      return { canPlace: true, score };
    }

    if (n.isCrazy || n.underCrazyMega) {
      score += 10;
      if (n.puppy !== null && n.puppy === placePuppy) score += 10;
      return { canPlace: true, score };
    }

    if (n.isFireHydrant) {
      return { canPlace: true, score: 5 };
    }

    // Reject: neighbor has a real puppy + color, neither matches.
    const colorPresent = n.color !== null;
    const puppyPresent = n.puppy !== null;
    if (
      !placeIsCrazy &&
      colorPresent &&
      puppyPresent &&
      n.color !== placeColor &&
      n.puppy !== placePuppy
    ) {
      return { canPlace: false, score: 0 };
    }

    // Score the matches.
    let colorMatch = false;
    if (colorPresent && (n.color === placeColor || placeIsCrazy)) {
      score += 10;
      colorMatch = true;
    }
    if (puppyPresent && n.puppy === placePuppy) {
      if (colorMatch) score += 10;
      score += 10;
    }
    return { canPlace: true, score };
  }

  // Atomic: check + mutate. Returns a diff suitable for the render layer.
  applyPlacement(
    index: number,
    puppy: PuppyId,
    color: Color,
    isCrazy: boolean,
    isHydrant: boolean,
  ): {
    placed: boolean;
    score: number;
    madeMega: boolean;
    madeSuperMega: boolean;
    megaTopLeftIndex: number;
    clearedRow: boolean;
    clearedCol: boolean;
    clearedIndexes: number[];
    removedMegaTopLefts: number[];
  } {
    const can = this.canPlacePuppy(index, puppy, color, isCrazy, isHydrant);
    if (!can.canPlace) {
      return {
        placed: false, score: 0, madeMega: false, madeSuperMega: false, megaTopLeftIndex: -1,
        clearedRow: false, clearedCol: false, clearedIndexes: [], removedMegaTopLefts: [],
      };
    }
    const sq = this.squareAt(index);
    sq.place(puppy, color);
    sq.data.isClear = true;

    // Row/column clear trumps MegaPuppy formation (mirrors Board.swift:2062 —
    // if a placement both completes a row/col AND forms a mega, the clear
    // happens and no mega is created).
    const clears = this.doesClearRowOrColumn(index);
    if (clears.row || clears.col) {
      const { clearedIndexes, removedMegaTopLefts } = this.clearRowOrColumn(
        index, clears.row, clears.col,
      );
      return {
        placed: true,
        score: can.score,
        madeMega: false,
        madeSuperMega: false,
        megaTopLeftIndex: -1,
        clearedRow: clears.row,
        clearedCol: clears.col,
        clearedIndexes,
        removedMegaTopLefts,
      };
    }

    const mega = this.doesMakeMegaPuppy(index, puppy, color);
    // When a MegaPuppy forms, mark all 4 underlying cells as `underCrazyMega`
    // so they act as WILD adjacents for future placements (mirrors iOS — see
    // MegaPuppy.swift::createMegaPuppy lines 88-91 + Square.swift::calcPlaceScore
    // which treats `underCrazyMega` cells like CrazySquares).
    if (mega.makesMegaPuppy) {
      const tl = mega.megaPuppyLeftTop;
      for (const i of [tl, tl + 1, tl + this.numSquaresHoriz, tl + 1 + this.numSquaresHoriz]) {
        this.squareAt(i).data.underCrazyMega = true;
      }
    }
    return {
      placed: true,
      score: can.score,
      madeMega: mega.makesMegaPuppy,
      madeSuperMega: mega.makesSuperMegaPuppy,
      megaTopLeftIndex: mega.megaPuppyLeftTop,
      clearedRow: false,
      clearedCol: false,
      clearedIndexes: [],
      removedMegaTopLefts: [],
    };
  }

  // Mirrors Board.swift::doesClearRowOrColumn (line 1516). True for a row
  // (resp. col) if every cell in it either has a puppy or is a fire hydrant
  // (the placed cell counts because place() has already run), and the row
  // (resp. col) contains no obstacles.
  doesClearRowOrColumn(squareIndex: number): { row: boolean; col: boolean } {
    const row = Math.floor(squareIndex / this.numSquaresHoriz);
    const col = squareIndex % this.numSquaresHoriz;
    let clearRow = true;
    let clearCol = true;

    for (let i = row * this.numSquaresHoriz; i < (row + 1) * this.numSquaresHoriz; i++) {
      const d = this.squareAt(i).data;
      if (d.puppy === null && !d.isFireHydrant) {
        clearRow = false;
        break;
      }
    }
    for (let i = col; i < this.numSquares; i += this.numSquaresHoriz) {
      const d = this.squareAt(i).data;
      if (d.puppy === null && !d.isFireHydrant) {
        clearCol = false;
        break;
      }
    }
    // Obstacles in the row/col disqualify (mirrors iOS line 1543-1551).
    for (const o of this.obstacles) {
      const oRow = Math.floor(o.index / this.numSquaresHoriz);
      const oCol = o.index % this.numSquaresHoriz;
      if (oRow === row) clearRow = false;
      if (oCol === col) clearCol = false;
      // 2-wide obstacles also occupy oCol + 1.
      if (o.widthInSquares === 2 && oCol + 1 === col) clearCol = false;
    }
    return { row: clearRow, col: clearCol };
  }

  // Mirrors Board.swift::clearFromSquareIndex (line 1650). Wipes every cell
  // in the row/col back to empty grass and removes any MegaPuppies whose
  // 2×2 footprint overlaps the cleared row/col. Returns the cleared cell
  // indexes plus the top-left indexes of any removed MegaPuppies so the
  // render layer can tear down their sprites.
  clearRowOrColumn(
    squareIndex: number,
    doClearRow: boolean,
    doClearCol: boolean,
  ): { clearedIndexes: number[]; removedMegaTopLefts: number[] } {
    const horiz = this.numSquaresHoriz;
    const targetRow = doClearRow ? Math.floor(squareIndex / horiz) : -1;
    const targetCol = doClearCol ? squareIndex % horiz : -1;

    // Remove MegaPuppies whose 2×2 box overlaps the cleared row or col. For
    // each removed mega we also strip `underCrazyMega` from all 4 of its
    // cells — otherwise the cells that fall OUTSIDE the cleared row/col
    // still read as wildcard slots and let mismatched puppies be placed
    // next to them. (The underlying puppy/color data set when the mega
    // formed is preserved, so those surviving cells re-emerge as normal
    // placed puppies. Cells INSIDE the cleared row/col get wiped fully by
    // the wipe loop below.)
    const removedMegaTopLefts: number[] = [];
    this.megaPuppies = this.megaPuppies.filter((mp) => {
      const overlapsRow = doClearRow && (mp.row === targetRow || mp.row + 1 === targetRow);
      const overlapsCol = doClearCol && (mp.col === targetCol || mp.col + 1 === targetCol);
      if (overlapsRow || overlapsCol) {
        removedMegaTopLefts.push(mp.index);
        for (const i of [mp.index, mp.index + 1, mp.index + horiz, mp.index + 1 + horiz]) {
          this.squareAt(i).data.underCrazyMega = false;
        }
        return false;
      }
      return true;
    });

    const cleared = new Set<number>();
    const wipe = (i: number): void => {
      if (cleared.has(i)) return;
      const d = this.squareAt(i).data;
      // Cells stay "mown" (isClear stays true) so the render layer shows
      // the lighter dirt/grass look. Fire hydrants in the cleared row/col
      // are wiped too, mirroring iOS Square.swift::clearSquare (line 861).
      d.puppy = null;
      d.color = null;
      d.isClear = true;
      d.isCrazy = false;
      d.isFireHydrant = false;
      d.underCrazyMega = false;
      d.hasBonus = false;
      cleared.add(i);
    };

    if (doClearRow) {
      for (let c = 0; c < horiz; c++) wipe(targetRow * horiz + c);
    }
    if (doClearCol) {
      for (let r = 0; r < this.numSquaresVert; r++) wipe(r * horiz + targetCol);
    }
    return { clearedIndexes: [...cleared], removedMegaTopLefts };
  }

  toJSON(): BoardSnapshot {
    return {
      numSquaresHoriz: this.numSquaresHoriz,
      numSquaresVert: this.numSquaresVert,
      squares: this.squares.map((s) => s.toJSON()),
      obstacles: this.obstacles.map((o) => ({ id: o.spec.id, atIndex: o.index })),
      bonusSquare: this.bonusSquare,
      megaPuppies: this.megaPuppies.map((mp) => mp.snapshot()),
    };
  }

  static fromJSON(s: BoardSnapshot): Board {
    const b = new Board(s.numSquaresHoriz, s.numSquaresVert);
    for (let i = 0; i < s.squares.length; i++) {
      const src = s.squares[i];
      if (!src) continue;
      b.squareAt(i).data = { ...src };
    }
    b.obstacles = s.obstacles.map((o) => new Obstacle(o.id, o.atIndex, s.numSquaresHoriz));
    b.bonusSquare = s.bonusSquare;
    if (s.megaPuppies) {
      b.megaPuppies = s.megaPuppies.map((snap) => MegaPuppy.fromSnapshot(snap));
    }
    b.syncUnderCrazyMegaFromMegaPuppies();
    return b;
  }

  // Re-stamp `underCrazyMega = true` on every cell covered by a MegaPuppy.
  // Idempotent. Used by the load paths so saves predating the flag-tracking
  // fix still treat MegaPuppy cells as WILD adjacents.
  syncUnderCrazyMegaFromMegaPuppies(): void {
    for (const mp of this.megaPuppies) {
      const tl = mp.index;
      if (tl < 0) continue;
      const cells = [tl, tl + 1, tl + this.numSquaresHoriz, tl + 1 + this.numSquaresHoriz];
      for (const i of cells) {
        if (i >= 0 && i < this.numSquares) {
          this.squareAt(i).data.underCrazyMega = true;
        }
      }
    }
  }

  // ------------------------------------------------------------------
  // Obstacle picker, level-clear, and weather/regrow live here as well.

  // Naive deterministic obstacle placer. The iOS version interleaves level
  // tuning we haven't ported yet; this version places `count` obstacles of the
  // given ids at distinct random empty indexes, given an injected RNG.
  placeObstacles(specs: { id: number; count: number }[], rng: Rng): void {
    const used = new Set<number>();
    const candidates: number[] = [];
    for (let i = 0; i < this.numSquares; i++) candidates.push(i);

    for (const { id, count } of specs) {
      for (let n = 0; n < count; n++) {
        // Re-filter: avoid used indexes plus, for 2-wide obstacles, anything
        // that would overlap or run off the right edge.
        const probe = new Obstacle(id, 0, this.numSquaresHoriz);
        const w = probe.widthInSquares;
        const valid = candidates.filter((idx) => {
          if (used.has(idx)) return false;
          if (w === 2) {
            if (idx % this.numSquaresHoriz === this.numSquaresHoriz - 1) return false;
            if (used.has(idx + 1)) return false;
          }
          return true;
        });
        if (valid.length === 0) return;
        const pick = valid[Math.floor(rng() * valid.length)]!;
        const obs = new Obstacle(id, pick, this.numSquaresHoriz);
        this.obstacles.push(obs);
        used.add(pick);
        if (obs.widthInSquares === 2) used.add(pick + 1);
      }
    }
  }

  // Place obstacles according to iOS Board.swift::createObstacles
  // (L734-773): per-level count + obstacle-ID distribution + fixed
  // candidate-index pool. Skips indices already occupied by a fire hydrant.
  placeObstaclesForLevel(levelNumber: number, rng: Rng): void {
    const numObstacles = NUM_OBSTACLES_PER_LEVEL[
      Math.min(Math.abs(levelNumber) - 1, NUM_OBSTACLES_PER_LEVEL.length - 1)
    ]!;
    if (numObstacles === 0) return;

    const candidatePool = [...OBSTACLE_INDEX_POOL];
    const used = new Set<number>();

    for (let n = 0; n < numObstacles; n++) {
      // ID distribution: pick 0..4 — 0=>merrygoround, 1=>horsey, 2=>parkbench,
      // others=>merrygoround. After level 10, 33% chance to upgrade to seesaw.
      let id = 0;
      const pick = Math.floor(rng() * 5);
      if (pick === 1) id = 3;       // Horsey
      else if (pick === 2) id = 1;  // Park Bench
      if (Math.abs(levelNumber) > 10 && Math.floor(rng() * 3) === 2) {
        id = 2;                     // See Saw (2 wide)
      }

      const probe = new Obstacle(id, 0, this.numSquaresHoriz);
      const w = probe.widthInSquares;
      const valid = candidatePool.filter((idx) => {
        if (used.has(idx)) return false;
        if (idx >= this.numSquares) return false;
        if (this.squareAt(idx).data.isFireHydrant) return false;
        if (w === 2) {
          if (idx % this.numSquaresHoriz === this.numSquaresHoriz - 1) return false;
          if (idx + 1 >= this.numSquares) return false;
          if (this.squareAt(idx + 1).data.isFireHydrant) return false;
          if (used.has(idx + 1)) return false;
        }
        return true;
      });
      if (valid.length === 0) return;
      const placedIdx = valid[Math.floor(rng() * valid.length)]!;
      const obs = new Obstacle(id, placedIdx, this.numSquaresHoriz);
      this.obstacles.push(obs);
      used.add(placedIdx);
      if (obs.widthInSquares === 2) used.add(placedIdx + 1);
    }
  }

  // Mirrors MegaPuppy.swift::doUseSpecialMegaPuppy. Tapping a SuperMega
  // increments the player's CrazySquares pile (handled at the caller) and
  // clears all 4 underlying cells back to mown grass — no CrazySquare tile
  // is left behind on the board itself. Returns the cleared indexes so the
  // render layer can sparkle them. Visual sprites are torn down separately.
  convertMegaToCrazy(topLeftIndex: number): number[] {
    const horiz = this.numSquaresHoriz;
    const indexes = [
      topLeftIndex,
      topLeftIndex + 1,
      topLeftIndex + horiz,
      topLeftIndex + 1 + horiz,
    ];
    for (const i of indexes) {
      const d = this.squareAt(i).data;
      d.puppy = null;
      d.color = null;
      d.isClear = true;
      d.isCrazy = false;
      d.isFireHydrant = false;
      d.underCrazyMega = false;
      d.hasBonus = false;
    }
    this.megaPuppies = this.megaPuppies.filter((mp) => mp.index !== topLeftIndex);
    return indexes;
  }

  spawnBonus(rng: Rng): number {
    const candidates: number[] = [];
    for (let i = 0; i < this.numSquares; i++) {
      const sq = this.squareAt(i).data;
      if (sq.isClear && sq.puppy === null && !this.obstacleAtIndex(i) && !sq.isFireHydrant) {
        candidates.push(i);
      }
    }
    if (candidates.length === 0) {
      this.bonusSquare = -1;
      return -1;
    }
    const pick = candidates[Math.floor(rng() * candidates.length)]!;
    this.squareAt(pick).data.hasBonus = true;
    this.bonusSquare = pick;
    return pick;
  }

  clearBonus(): void {
    if (this.bonusSquare >= 0) {
      this.squareAt(this.bonusSquare).data.hasBonus = false;
    }
    this.bonusSquare = -1;
  }

  obstacleSummary(): { id: number; index: number }[] {
    return this.obstacles.map((o) => ({ id: o.spec.id, index: o.index }));
  }

  // Level is clear when every non-obstacle square is cleared.
  isLevelClear(): boolean {
    for (let i = 0; i < this.numSquares; i++) {
      if (this.obstacleAtIndex(i)) continue;
      if (!this.squareAt(i).data.isClear) return false;
    }
    return true;
  }

  // True if any of the four candidate indexes is already covered by an
  // existing MegaPuppy. Used by doesMakeMegaPuppy to refuse forming a new MP
  // that would partially overlap an existing one.
  private mpCovers(i1: number, i2: number, i3: number, i4: number): boolean {
    return this.megaPuppies.some((mp) => {
      return (
        mp.indexInMegaPuppy(i1, this.numSquaresHoriz) ||
        mp.indexInMegaPuppy(i2, this.numSquaresHoriz) ||
        mp.indexInMegaPuppy(i3, this.numSquaresHoriz) ||
        mp.indexInMegaPuppy(i4, this.numSquaresHoriz)
      );
    });
  }

  // Mirrors Board.swift::doesMakeMegaPuppy. The played puppy could be the
  // top-left, top-right, bottom-left, or bottom-right corner of a 2x2.
  // Returns the *would-be* MP top-left index when a formation occurs.
  doesMakeMegaPuppy(
    squareIndex: number,
    puppy: PuppyId | null,
    color: Color | null,
  ): { makesMegaPuppy: boolean; makesSuperMegaPuppy: boolean; megaPuppyLeftTop: number } {
    if (puppy === null || color === null) {
      return { makesMegaPuppy: false, makesSuperMegaPuppy: false, megaPuppyLeftTop: -1 };
    }
    const horiz = this.numSquaresHoriz;
    const vert = this.numSquaresVert;
    const row = Math.floor(squareIndex / horiz);
    const col = squareIndex % horiz;

    // The focal square is empty at the moment of this call (we haven't placed
    // yet). We compare the placed (puppy, color) against the OTHER 3 cells.
    const tryCorner = (
      cell1: number,
      cell2: number,
      cell3: number,
      topLeft: number,
      allFour: [number, number, number, number],
    ): { makesMegaPuppy: boolean; makesSuperMegaPuppy: boolean; megaPuppyLeftTop: number } | null => {
      if (this.mpCovers(allFour[0], allFour[1], allFour[2], allFour[3])) return null;
      const a = this.squareAt(cell1).data;
      const b = this.squareAt(cell2).data;
      const c = this.squareAt(cell3).data;
      if (puppy === a.puppy && puppy === b.puppy && puppy === c.puppy) {
        const sameColor = color === a.color && color === b.color && color === c.color;
        return { makesMegaPuppy: true, makesSuperMegaPuppy: sameColor, megaPuppyLeftTop: topLeft };
      }
      return null;
    };

    // Top-left of the 2x2: focal at squareIndex, others at +1, +horiz, +1+horiz.
    if (col < horiz - 1 && row < vert - 1) {
      const r = tryCorner(
        squareIndex + 1,
        squareIndex + horiz,
        squareIndex + 1 + horiz,
        squareIndex,
        [squareIndex, squareIndex + 1, squareIndex + horiz, squareIndex + 1 + horiz],
      );
      if (r) return r;
    }
    // Bottom-right: focal at squareIndex, others at -1, -horiz, -1-horiz.
    if (col > 0 && row > 0) {
      const tl = squareIndex - 1 - horiz;
      const r = tryCorner(
        squareIndex - 1,
        squareIndex - horiz,
        squareIndex - 1 - horiz,
        tl,
        [squareIndex, squareIndex - 1, squareIndex - horiz, squareIndex - 1 - horiz],
      );
      if (r) return r;
    }
    // Top-right: focal at squareIndex, others at -1, +horiz, -1+horiz.
    if (col > 0 && row < vert - 1) {
      const tl = squareIndex - 1;
      const r = tryCorner(
        squareIndex - 1,
        squareIndex + horiz,
        squareIndex - 1 + horiz,
        tl,
        [squareIndex, squareIndex - 1, squareIndex + horiz, squareIndex - 1 + horiz],
      );
      if (r) return r;
    }
    // Bottom-left: focal at squareIndex, others at +1, -horiz, +1-horiz.
    if (col < horiz - 1 && row > 0) {
      const tl = squareIndex - horiz;
      const r = tryCorner(
        squareIndex + 1,
        squareIndex - horiz,
        squareIndex + 1 - horiz,
        tl,
        [squareIndex, squareIndex + 1, squareIndex - horiz, squareIndex + 1 - horiz],
      );
      if (r) return r;
    }

    return { makesMegaPuppy: false, makesSuperMegaPuppy: false, megaPuppyLeftTop: -1 };
  }

  // Pick `count` random cleared (non-occupied, non-obstacle) cells and regrow them.
  regrowRandomCleared(count: number, rng: Rng): { index: number }[] {
    const cleared: number[] = [];
    for (let i = 0; i < this.numSquares; i++) {
      const sq = this.squareAt(i).data;
      if (sq.isClear && sq.puppy === null && !this.obstacleAtIndex(i) && !sq.isFireHydrant) {
        cleared.push(i);
      }
    }
    const picked: { index: number }[] = [];
    for (let i = 0; i < count && cleared.length > 0; i++) {
      const idx = Math.floor(rng() * cleared.length);
      const [chosen] = cleared.splice(idx, 1);
      if (chosen === undefined) break;
      const sq = this.squareAt(chosen).data;
      sq.isClear = false;
      sq.color = null;
      picked.push({ index: chosen });
    }
    return picked;
  }
}
